import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

// Token can live in either localStorage (remember-me on) or sessionStorage.
const TOKEN_KEY = "vsm_token";
const USER_KEY = "vsm_user";
const REMEMBER_KEY = "vsm_remember_username";

function readStored(key) {
  return localStorage.getItem(key) || sessionStorage.getItem(key);
}

function writeStored(key, value, persist) {
  // Always clear the other store to avoid stale tokens
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
  if (persist) localStorage.setItem(key, value);
  else sessionStorage.setItem(key, value);
}

function clearStored(key) {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load persisted auth on mount only. TOKEN_KEY/USER_KEY/clearStored are
  // module-level constants and the effect is intentionally one-shot.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const tok = readStored(TOKEN_KEY);
    const u = readStored(USER_KEY);
    if (tok && u) {
      try {
        setUser(JSON.parse(u));
      } catch (e) {
        clearStored(USER_KEY);
      }
    }
    setLoading(false);
  }, []);

  const login = async (username, password, remember = false) => {
    const { data } = await api.post("/auth/login", { username, password });
    writeStored(TOKEN_KEY, data.access_token, remember);
    writeStored(USER_KEY, JSON.stringify(data.user), remember);
    if (remember) {
      localStorage.setItem(REMEMBER_KEY, data.user.username);
    } else {
      localStorage.removeItem(REMEMBER_KEY);
    }
    setUser(data.user);
    return data.user;
  };

  // Patch the in-memory user object (e.g. after the Profile page updates the
  // name / photo). Persists to whichever storage holds the existing user blob.
  const updateUser = (patch) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...patch };
      const persist = !!localStorage.getItem(TOKEN_KEY);
      writeStored(USER_KEY, JSON.stringify(next), persist);
      return next;
    });
  };

  const logout = () => {
    clearStored(TOKEN_KEY);
    clearStored(USER_KEY);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={useMemo(() => ({ user, loading, login, logout, updateUser }), [user, loading])}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// Helper used by the login form to pre-fill the remembered username
export const getRememberedUsername = () => localStorage.getItem(REMEMBER_KEY) || "";
